import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { ICampaign } from '../../redux/buzzbridge/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EsBackButton } from '../EsButtons/EsBackButton';
import { PlatformApiIntegrationTrust } from './PlatformApiIntegrationTrust';
import { CampaignFillingFastBadge } from './CampaignFillingFastBadge';
import { isPublicCampaignFillingFast } from '../../utils/campaignParticipationGate';
import { useCreatorCampaignPayoutDisplay } from '../../hook/useCreatorCampaignPayoutDisplay';
import { CreatorPayoutAmount } from './CreatorPayoutAmount';

interface Props {
    campaign: ICampaign;
    onBackButtonPressed?: () => void;
}

const daysUntil = (dateStr?: string) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
    if (diff <= 0) return 'Ended';
    return `Ends in ${diff} day${diff > 1 ? 's' : ''}`;
};

const locationLabel = (campaign: ICampaign) => {
    const loc = campaign.location;
    if (!loc) return 'Remote';
    return loc.short_name || loc.description || loc.address || 'Remote';
};

export const CampaignDetailHero: React.FC<Props> = ({ campaign, onBackButtonPressed }) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const { netAmountFormatted, estimatedTotalFormatted, basePayFormatted, estimatedCpaFormatted } =
        useCreatorCampaignPayoutDisplay(campaign);
    const heroImage = campaign.feature_image || campaign.assets?.[0]?.url;
    const endLabel = daysUntil(campaign.end_date);
    const fillingFast = isPublicCampaignFillingFast(campaign);

    return (
        <Box
            sx={{
                position: 'relative',
                height: { xs: 460, md: 530 },
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                borderRadius: 0,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
            }}>
            {heroImage ? (
                <Box
                    component="img"
                    src={heroImage}
                    alt={campaign.title}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'grayscale(0.2) brightness(0.75)',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${t.highlight}30 0%, ${t.pure_black} 100%)`,
                    }}
                />
            )}

            {/* Gradient overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 0%, ${t.pure_black} 100%)`,
                }}
            />

            {/* Top bar: back (same pattern as contest detail hero) */}
            {onBackButtonPressed && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        p: { xs: 2, md: 3 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        zIndex: 2,
                    }}>
                    <EsBackButton handleDetails={onBackButtonPressed} />
                </Box>
            )}

            {/* Content + secure integration (bottom-right on all breakpoints) */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: { xs: 2.5, md: 8 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'flex-end' },
                    justifyContent: 'space-between',
                    gap: { xs: 2, md: 4 },
                    zIndex: 2,
                }}>
                <Box sx={{ flex: 1, minWidth: 0, maxWidth: { md: 900 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        {campaign.status === 'active' && (
                            <Chip
                                label="Active Campaign"
                                size="small"
                                sx={{
                                    bgcolor: `${t.btnCustomColor}18`,
                                    border: `1px solid ${t.btnCustomColor}33`,
                                    color: t.btnCustomColor,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}
                            />
                        )}
                        {fillingFast && <CampaignFillingFastBadge />}
                        {endLabel && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTime sx={{ fontSize: 14, color: t.subtle_white }} />
                                <Typography
                                    sx={{
                                        color: t.subtle_white,
                                        fontSize: 12,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        letterSpacing: '0.03em',
                                    }}>
                                    {endLabel}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Typography
                        sx={{
                            fontSize: { xs: 32, md: 56 },
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: t.true_white,
                            letterSpacing: '-0.03em',
                            lineHeight: 1,
                            mb: 3,
                        }}>
                        {campaign.title}
                        {campaign.reward && (
                            <Box component="span" sx={{ color: t.highlight }}>
                                {' '}
                                UGC Series
                            </Box>
                        )}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, md: 5 }, alignItems: 'center' }}>
                        <Box>
                            <Typography
                                sx={{
                                    color: t.subtle_white,
                                    fontSize: 10,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.15em',
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                Possible Earning
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 24,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: t.highlight,
                                }}>
                                <CreatorPayoutAmount
                                    total={estimatedTotalFormatted}
                                    base={basePayFormatted}
                                    additional={estimatedCpaFormatted}
                                    fallback={netAmountFormatted ?? campaign.possible_earning}
                                />
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    color: t.subtle_white,
                                    fontSize: 10,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.15em',
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                Location
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 24,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: t.true_white,
                                }}>
                                {locationLabel(campaign)}
                            </Typography>
                        </Box>
                        {campaign.is_clipping && (
                            <Box>
                                <Typography
                                    sx={{
                                        color: t.subtle_white,
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.15em',
                                        fontWeight: 700,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Type
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 24,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: t.true_white,
                                    }}>
                                    Editors
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        alignSelf: { xs: 'stretch', md: 'flex-end' },
                        width: '100%',
                        maxWidth: { md: 480 },
                    }}>
                    <PlatformApiIntegrationTrust compact layout="row" />
                </Box>
            </Box>
        </Box>
    );
};
